// src/sim/ai/coordination/utility.ts — AI-12 investigate-division utility scorer (D-16, BLOCKER-1).
//
// Computes the utility score for a hunter's investigate-sighting branch given a specific
// blackboard entry and a count of other hunters already heading to the same entry.
//
// Formula (D-16):
//   utility = P4_INVESTIGATE_BASE
//             − P4_INVESTIGATE_DISTANCE_WEIGHT * tileDistanceCost / maxMapTiles
//             − P4_INVESTIGATE_DUPLICATE_WEIGHT * huntersAlreadyHeadingHereCount
//             + P4_INVESTIGATE_CONFIDENCE_WEIGHT * confidenceScalar
//
// where confidenceScalar = high→1.0 / medium→0.5 / low→0.2.
//
// Pitfall 13: tileGraphDistanceCost calls A* but returns ONLY the cost scalar —
// no full path materialised. Used N×K times per decide-tick (N=hunters K=entries).
//
// Source: .planning/phases/04-ai/04-CONTEXT.md D-16; 04-10-PLAN.md Step 5d.

import type { Perception } from '../../../shared/types/perception';
import type { BlackboardEntry } from '../../../shared/types/world';
import { tileGraphDistance } from '../pathing';
// P7 POL-06 / D-12: investigate utility weights read through the tuning cache
// (./constants are the fallback defaults).
import { getTuning } from '../../content/cache';

/**
 * Compute the investigate-sighting branch utility for a single hunter + blackboard entry pair.
 *
 * @param p                            — hunter's current Perception (for self position + map)
 * @param entry                        — blackboard entry being evaluated
 * @param huntersAlreadyHeadingHereCount — number of OTHER hunters already targeting this entry (D-16)
 */
export function computeInvestigateUtility(
  p: Perception,
  entry: BlackboardEntry,
  huntersAlreadyHeadingHereCount: number,
): number {
  // Confidence scalar: high=1.0, medium=0.5, low=0.2
  const conf =
    entry.confidence === 'high'
      ? 1.0
      : entry.confidence === 'medium'
        ? 0.5
        : 0.2;

  // maxMapTiles = width + height (proxy for max path cost across the map)
  const maxMapTiles = p.map.widthInTiles + p.map.heightInTiles;

  // Distance in tiles from hunter to entry position (A* cost, no full path materialised).
  const selfTileX = Math.floor(p.self.position.x / p.map.tileWidth);
  const selfTileY = Math.floor(p.self.position.y / p.map.tileHeight);
  const entryTileX = Math.floor(entry.position.x / p.map.tileWidth);
  const entryTileY = Math.floor(entry.position.y / p.map.tileHeight);

  // tileGraphDistance bypasses the pathing throttle/cache — pure cost query (Pitfall 13).
  // Phase 08.1 Item 2: use movementGrid for accurate routing cost (obstacles/fences
  // lengthen paths that cross them). Falls back to sight grid for minimal MapData.
  const distCost = tileGraphDistance(selfTileX, selfTileY, entryTileX, entryTileY, p.map.movementGrid ?? p.map.grid);

  // Normalise: clamp to [0,1] so infinite / very-long paths reduce score without NaN.
  const distTilesNorm = Math.min(1, isFinite(distCost) ? distCost / maxMapTiles : 1);

  const t = getTuning();
  return (
    t.investigateBase
    - t.investigateDistanceWeight * distTilesNorm
    - t.investigateDuplicateWeight * huntersAlreadyHeadingHereCount
    + t.investigateConfidenceWeight * conf
  );
}
